import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Plus,
  Loader2,
  ClipboardCheck,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  ChevronRight,
  Calendar,
  Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const responseConfig = {
  improved: { label: 'Improved', color: 'bg-success/10 text-success border-success/20', icon: TrendingUp },
  partial: { label: 'Partial', color: 'bg-warning/10 text-warning border-warning/20', icon: Minus },
  none: { label: 'No Change', color: 'bg-muted text-muted-foreground border-border', icon: Minus },
  worse: { label: 'Worse', color: 'bg-destructive/10 text-destructive border-destructive/20', icon: TrendingDown },
};

const adherenceConfig = {
  good: { label: 'Good', color: 'bg-success/10 text-success border-success/20' },
  partial: { label: 'Partial', color: 'bg-warning/10 text-warning border-warning/20' },
  poor: { label: 'Poor', color: 'bg-destructive/10 text-destructive border-destructive/20' },
};

function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
}

export default function FollowUpsListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterResponse, setFilterResponse] = useState('all');

  // Create modal state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [patients, setPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [consultationOptions, setConsultationOptions] = useState([]);
  const [selectedConsultationId, setSelectedConsultationId] = useState('');
  const [selectedPrescriptionId, setSelectedPrescriptionId] = useState('');
  const [createForm, setCreateForm] = useState({
    response: '',
    side_effects: '',
    adherence: 'good',
    notes: ''
  });
  const [loadingConsultations, setLoadingConsultations] = useState(false);

  const fetchFollowUps = useCallback(async () => {
    try {
      const params = filterResponse !== 'all' ? `?response_filter=${filterResponse}` : '';
      const { data } = await axios.get(`${API_URL}/api/follow-ups${params}`, { withCredentials: true });
      setFollowUps(data);
    } catch (error) {
      toast.error('Failed to fetch follow-ups');
    } finally {
      setLoading(false);
    }
  }, [filterResponse]);

  const fetchPatients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/patients?is_active=true`, { withCredentials: true });
      setPatients(data);
    } catch (error) {
      console.error('Failed to fetch patients', error);
    }
  }, []);

  useEffect(() => {
    fetchFollowUps();
    fetchPatients();
  }, [fetchFollowUps, fetchPatients]);

  const handlePatientChange = async (patientId) => {
    setSelectedPatientId(patientId);
    setSelectedConsultationId('');
    setSelectedPrescriptionId('');
    setConsultationOptions([]);
    if (!patientId) return;

    setLoadingConsultations(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/follow-ups/patient/${patientId}/consultations`, { withCredentials: true });
      setConsultationOptions(data);
    } catch (error) {
      toast.error('Failed to fetch consultations');
    } finally {
      setLoadingConsultations(false);
    }
  };

  const handleConsultationChange = (consultationId) => {
    setSelectedConsultationId(consultationId);
    setSelectedPrescriptionId('');
  };

  const selectedConsultation = consultationOptions.find(c => c.id === selectedConsultationId);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!selectedPatientId || !selectedConsultationId || !createForm.response) {
      toast.error('Please fill all required fields');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        patient_id: selectedPatientId,
        consultation_id: selectedConsultationId,
        prescription_id: selectedPrescriptionId || null,
        ...createForm
      };
      const { data } = await axios.post(`${API_URL}/api/follow-ups`, payload, { withCredentials: true });
      toast.success('Follow-up recorded');
      setIsCreateOpen(false);
      resetCreateForm();
      navigate(`/follow-ups/${data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create follow-up');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetCreateForm = () => {
    setSelectedPatientId('');
    setSelectedConsultationId('');
    setSelectedPrescriptionId('');
    setConsultationOptions([]);
    setCreateForm({ response: '', side_effects: '', adherence: 'good', notes: '' });
  };

  const stats = {
    total: followUps.length,
    improved: followUps.filter(f => f.response === 'improved').length,
    needsAttention: followUps.filter(f => f.response === 'worse' || f.response === 'none').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="follow-ups-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="follow-ups-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-medium tracking-tight text-foreground">Follow-Ups</h1>
          <p className="text-muted-foreground mt-1">Track patient outcomes and treatment adherence</p>
        </div>
        <Button
          onClick={() => setIsCreateOpen(true)}
          className="bg-primary hover:bg-primary/90 rounded-full"
          data-testid="create-follow-up-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Record Follow-Up
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <ClipboardCheck className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold" data-testid="stat-total">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total Follow-Ups</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-success/10">
              <TrendingUp className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold" data-testid="stat-improved">{stats.improved}</p>
              <p className="text-xs text-muted-foreground">Improved</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-destructive/10">
              <AlertTriangle className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold" data-testid="stat-needs-attention">{stats.needsAttention}</p>
              <p className="text-xs text-muted-foreground">Needs Attention</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Filter className="w-4 h-4 text-muted-foreground" />
        <Select value={filterResponse} onValueChange={(val) => setFilterResponse(val)}>
          <SelectTrigger className="w-[180px] rounded-xl" data-testid="filter-response">
            <SelectValue placeholder="Filter by response" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Responses</SelectItem>
            <SelectItem value="improved">Improved</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="none">No Change</SelectItem>
            <SelectItem value="worse">Worse</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      <div className="space-y-4">
        {followUps.length === 0 ? (
          <Card className="border-border shadow-sm">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <ClipboardCheck className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-center">No follow-ups recorded yet.</p>
            </CardContent>
          </Card>
        ) : (
          followUps.map((fu) => {
            const rc = responseConfig[fu.response] || responseConfig.none;
            const ac = adherenceConfig[fu.adherence] || adherenceConfig.good;
            const RIcon = rc.icon;
            return (
              <Card
                key={fu.id}
                className="border-border shadow-sm card-interactive cursor-pointer"
                onClick={() => navigate(`/follow-ups/${fu.id}`)}
                data-testid={`follow-up-card-${fu.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <RIcon className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading font-medium">{fu.patient_name}</h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(fu.created_at)}</span>
                          <span>·</span>
                          <span>Dr. {fu.doctor_name.replace('Dr. ', '')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className={rc.color} data-testid={`response-badge-${fu.id}`}>
                        {rc.label}
                      </Badge>
                      <Badge variant="outline" className={ac.color}>
                        Adherence: {ac.label}
                      </Badge>
                      {fu.side_effects && (
                        <Badge variant="outline" className="bg-warning/10 text-warning border-warning/20 text-xs">
                          Side Effects
                        </Badge>
                      )}
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Create Follow-Up Modal */}
      <Dialog open={isCreateOpen} onOpenChange={(open) => { if (!open) resetCreateForm(); setIsCreateOpen(open); }}>
        <DialogContent className="sm:max-w-lg" data-testid="create-follow-up-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Record Follow-Up</DialogTitle>
            <DialogDescription>Select a patient and their consultation to record outcomes</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            {/* Patient */}
            <div className="space-y-2">
              <Label>Patient *</Label>
              <Select value={selectedPatientId} onValueChange={handlePatientChange}>
                <SelectTrigger className="rounded-xl" data-testid="select-patient-follow-up">
                  <SelectValue placeholder="Select a patient" />
                </SelectTrigger>
                <SelectContent>
                  {patients.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name} ({p.phone})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Consultation */}
            {selectedPatientId && (
              <div className="space-y-2">
                <Label>Linked Consultation *</Label>
                {loadingConsultations ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground p-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading consultations...
                  </div>
                ) : consultationOptions.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-2">No completed consultations found for this patient.</p>
                ) : (
                  <Select value={selectedConsultationId} onValueChange={handleConsultationChange}>
                    <SelectTrigger className="rounded-xl" data-testid="select-consultation-follow-up">
                      <SelectValue placeholder="Select a consultation" />
                    </SelectTrigger>
                    <SelectContent>
                      {consultationOptions.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {formatDate(c.consultation_date)} — {c.consultation_type} {c.diagnosis ? `(${c.diagnosis.slice(0, 30)})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {/* Prescription (auto from consultation) */}
            {selectedConsultation && selectedConsultation.prescriptions.length > 0 && (
              <div className="space-y-2">
                <Label>Linked Prescription</Label>
                <Select value={selectedPrescriptionId} onValueChange={setSelectedPrescriptionId}>
                  <SelectTrigger className="rounded-xl" data-testid="select-prescription-follow-up">
                    <SelectValue placeholder="Select a prescription (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedConsultation.prescriptions.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {formatDate(p.created_at)} — {p.medicine_count} medicines — {p.doctor_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Response */}
            <div className="space-y-2">
              <Label>Response *</Label>
              <Select value={createForm.response} onValueChange={(val) => setCreateForm(p => ({ ...p, response: val }))}>
                <SelectTrigger className="rounded-xl" data-testid="select-response">
                  <SelectValue placeholder="Select response" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="improved">Improved</SelectItem>
                  <SelectItem value="partial">Partial Improvement</SelectItem>
                  <SelectItem value="none">No Change</SelectItem>
                  <SelectItem value="worse">Worse</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Adherence */}
            <div className="space-y-2">
              <Label>Adherence</Label>
              <Select value={createForm.adherence} onValueChange={(val) => setCreateForm(p => ({ ...p, adherence: val }))}>
                <SelectTrigger className="rounded-xl" data-testid="select-adherence">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="good">Good</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="poor">Poor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Side Effects */}
            <div className="space-y-2">
              <Label>Side Effects</Label>
              <Textarea
                value={createForm.side_effects}
                onChange={(e) => setCreateForm(p => ({ ...p, side_effects: e.target.value }))}
                placeholder="Describe any side effects..."
                className="rounded-xl min-h-[60px]"
                data-testid="side-effects-input"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={createForm.notes}
                onChange={(e) => setCreateForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Additional notes..."
                className="rounded-xl min-h-[60px]"
                data-testid="follow-up-notes-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { resetCreateForm(); setIsCreateOpen(false); }} className="rounded-full">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !selectedPatientId || !selectedConsultationId || !createForm.response}
                className="bg-primary hover:bg-primary/90 rounded-full"
                data-testid="create-follow-up-submit"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Record Follow-Up
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
